import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AlertTriangle, Bell, CheckSquare, X } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const AdminAnnouncementPopup = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const response = await axios.get(`${API}/user/announcements`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnnouncements(response.data.announcements || []);
      } catch (error) {
        console.error("Failed to fetch announcements:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchAnnouncements();
  }, [token]);

  const handleConfirm = async () => {
    if (!confirmChecked) {
      toast.error("Please check the box to confirm you've read this announcement");
      return;
    }
    
    const currentAnnouncement = announcements[currentIndex];
    
    try {
      await axios.post(`${API}/user/announcements/${currentAnnouncement.announcement_id}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Move to next announcement or close
      if (currentIndex < announcements.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setConfirmChecked(false);
      } else {
        onClose?.();
      }
    } catch (error) {
      toast.error("Failed to confirm announcement");
    }
  };

  if (loading) return null;
  if (announcements.length === 0) return null;

  const currentAnnouncement = announcements[currentIndex];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" data-testid="admin-announcement-popup">
      <div className="bg-[#C0C0C0] rounded-lg shadow-2xl w-full max-w-lg overflow-hidden border-4 border-[#808080]">
        {/* Title Bar */}
        <div className={`px-4 py-2 flex items-center justify-between ${
          currentAnnouncement.priority === 'urgent' ? 'bg-gradient-to-r from-red-600 to-red-700' :
          currentAnnouncement.priority === 'important' ? 'bg-gradient-to-r from-orange-500 to-amber-600' :
          'bg-gradient-to-r from-blue-600 to-indigo-700'
        } text-white`}>
          <div className="flex items-center gap-2">
            {currentAnnouncement.priority === 'urgent' ? (
              <AlertTriangle className="w-5 h-5 animate-pulse" />
            ) : (
              <Bell className="w-5 h-5" />
            )}
            <span className="font-bold">
              {currentAnnouncement.priority === 'urgent' ? '🚨 URGENT ANNOUNCEMENT' :
               currentAnnouncement.priority === 'important' ? '⚠️ IMPORTANT ANNOUNCEMENT' :
               '📢 Admin Announcement'}
            </span>
          </div>
          <span className="text-xs opacity-80">
            {currentIndex + 1} of {announcements.length}
          </span>
        </div>

        {/* Content */}
        <div className="p-4">
          <h2 className="text-lg font-bold text-gray-800 mb-2">
            {currentAnnouncement.subject}
          </h2>
          
          <div className="bg-white rounded-lg p-4 mb-4 max-h-60 overflow-y-auto retro-scrollbar border border-gray-300">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {currentAnnouncement.content}
            </p>
          </div>
          
          <div className="text-xs text-gray-500 mb-4">
            From: <strong>{currentAnnouncement.sender_username}</strong> • 
            {new Date(currentAnnouncement.created_at).toLocaleString()}
          </div>

          {/* Confirmation checkbox */}
          <label className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              className="w-5 h-5 mt-0.5 accent-green-600"
              data-testid="confirm-read-checkbox"
            />
            <span className="text-sm font-medium text-gray-800">
              I have read and understood this announcement
            </span>
          </label>
        </div>

        {/* Action Button */}
        <div className="px-4 pb-4">
          <button
            onClick={handleConfirm}
            disabled={!confirmChecked}
            className={`w-full py-3 rounded-lg font-bold text-white transition-all flex items-center justify-center gap-2 ${
              confirmChecked 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 cursor-pointer' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            data-testid="confirm-announcement-btn"
          >
            <CheckSquare className="w-5 h-5" />
            {currentIndex < announcements.length - 1 
              ? `Confirm & View Next (${announcements.length - currentIndex - 1} more)`
              : 'Confirm & Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncementPopup;
